import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaSearch, FaMapMarkerAlt, FaClock, FaBriefcase, 
  FaRupeeSign, FaHeart, FaUsers, FaRocket, 
  FaTrophy, FaCoffee, FaGamepad, FaArrowRight 
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import './CareerPage.css';

const CareerPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');

  const departments = ['all', 'Engineering', 'Design', 'Product', 'Marketing', 'Sales', 'HR'];
  const locations = ['all', 'Bangalore', 'Mumbai', 'Delhi', 'Pune', 'Remote'];

  const jobs = [
    {
      id: 1,
      title: 'Frontend Developer (Fresher)',
      slug: 'frontend-developer-fresher',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      experience: '0-1 years',
      salary: '2-4 LPA',
      description: 'Looking for passionate freshers with knowledge of React and modern web technologies.',
      requirements: [
        'Basic knowledge of React.js',
        'HTML, CSS, JavaScript',
        'Eagerness to learn',
        'Good problem-solving skills'
      ],
      posted: 'Recently',
      icon: '⚛️'
    },
    {
      id: 2,
      title: 'Backend Developer Intern',
      slug: 'backend-developer-intern',
      department: 'Engineering',
      location: 'Remote',
      type: 'Internship',
      experience: '0-1 years',
      salary: 'Stipend Based',
      description: 'Internship opportunity for backend development using Node.js / Python.',
      requirements: [
        'Basic Node.js or Python',
        'Understanding of APIs',
        'Database basics',
        'Willing to learn'
      ],
      posted: 'Recently',
      icon: '⚙️'
    },
    {
      id: 3,
      title: 'Business Development Executive (BDE)',
      slug: 'business-development-executive',
      department: 'Sales',
      location: 'Remote',
      type: 'Full-time',
      experience: '0-2 years',
      salary: '3-6 LPA + Incentives',
      description: 'We are hiring BDEs to generate leads, connect with clients, and grow our business.',
      requirements: [
        'Good communication skills',
        'Client handling',
        'Basic sales knowledge',
        'Confident personality'
      ],
      posted: 'Recently',
      icon: '💼'
    },
    {
      id: 4,
      title: 'HR Executive',
      slug: 'hr-executive',
      department: 'HR',
      location: 'Remote',
      type: 'Full-time',
      experience: '0-2 years',
      salary: '2-5 LPA',
      description: 'Looking for HR to manage hiring, employee engagement, and company culture.',
      requirements: [
        'Good communication',
        'Basic HR knowledge',
        'Recruitment process understanding',
        'People management skills'
      ],
      posted: 'Recently',
      icon: '🤝'
    },
    {
      id: 5,
      title: 'Digital Marketing Intern',
      slug: 'digital-marketing-intern',
      department: 'Marketing',
      location: 'Remote',
      type: 'Internship',
      experience: '0-1 years',
      salary: 'Stipend Based',
      description: 'Work on social media, SEO, and digital campaigns.',
      requirements: [
        'Basic social media knowledge',
        'Content creation',
        'SEO basics',
        'Creative mindset'
      ],
      posted: 'Recently',
      icon: '📈'
    }
  ];

  const perks = [
    { icon: <FaHeart />, title: 'Health Insurance', description: 'Comprehensive coverage for you and family' },
    { icon: <FaRocket />, title: 'Learning Budget', description: '₹50,000/year for courses and conferences' },
    { icon: <FaCoffee />, title: 'Free Meals', description: 'Breakfast, lunch, and unlimited snacks' },
    { icon: <FaGamepad />, title: 'Fun Activities', description: 'Game nights, team outings, and more' },
    { icon: <FaTrophy />, title: 'Performance Bonus', description: 'Quarterly bonuses based on impact' },
    { icon: <FaUsers />, title: 'Great Culture', description: 'Collaborative and inclusive environment' }
  ];

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = selectedDepartment === 'all' || job.department === selectedDepartment;
    const matchesLocation = selectedLocation === 'all' || job.location === selectedLocation;
    return matchesSearch && matchesDepartment && matchesLocation;
  });

  // Function to create URL-friendly slug
  const createApplyUrl = (slug, title) => {
    // Ensure slug is URL encoded for special characters
    return `/apply/${encodeURIComponent(slug)}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="career-page"
    >
      <Helmet>
        <title>Careers at ZustandTech | Join Our Software Development Team</title>
        <meta
          name="description"
          content="Explore open positions at ZustandTech (Zustand Tech Software Private Limited). Build your career in web development, mobile apps, AI, and cloud technology with a growing software company in India."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.zustandtech.com/careers" />

        <meta property="og:title" content="Careers at ZustandTech" />
        <meta
          property="og:description"
          content="Explore open positions and build your career at ZustandTech."
        />
        <meta property="og:url" content="https://www.zustandtech.com/careers" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Why Join Us */}
      <section className="why-join-us">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="page-heading"
          >
            <h1>Join Our <span className="gradient-text">Team</span></h1>
            <p>Build the future with us. Explore exciting career opportunities at ZustandTech.</p>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="section-title"
          >
            Why <span className="gradient-text">Join Us</span>
          </motion.h2>
          <div className="perks-grid">
            {perks.map((perk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="perk-card"
              >
                <div className="perk-icon">{perk.icon}</div>
                <h3>{perk.title}</h3>
                <p>{perk.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="open-positions">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="section-title"
          >
            Open <span className="gradient-text">Positions</span>
          </motion.h2>

          {/* Filters */}
          <div className="job-filters">
            <div className="search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <select 
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="filter-select"
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>
                    {dept === 'all' ? 'All Departments' : dept}
                  </option>
                ))}
              </select>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="filter-select"
              >
                {locations.map(loc => (
                  <option key={loc} value={loc}>
                    {loc === 'all' ? 'All Locations' : loc}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Jobs Grid */}
          <div className="jobs-grid">
            {filteredJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="job-card"
              >
                <div className="job-icon">{job.icon}</div>
                <div className="job-content">
                  <h3>{job.title}</h3>
                  <div className="job-tags">
                    <span><FaBriefcase /> {job.department}</span>
                    <span><FaMapMarkerAlt /> {job.location}</span>
                    <span><FaClock /> {job.type}</span>
                    <span><FaRupeeSign /> {job.salary}</span>
                  </div>
                  <p className="job-description">{job.description}</p>
                  <div className="job-requirements">
                    <h4>Requirements:</h4>
                    <ul>
                      {job.requirements.map((req, i) => (
                        <li key={i}>{req}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="job-footer">
                    <span className="job-posted">Posted {job.posted}</span>
                    <Link 
                      to={createApplyUrl(job.slug, job.title)} 
                      className="apply-btn"
                    >
                      Apply Now <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="no-jobs">
              <h3>No positions found</h3>
              <p>Try adjusting your filters or check back later</p>
            </div>
          )}
        </div>
      </section>

      {/* Culture Section */}
      <section className="culture-section">
        <div className="container">
          <div className="culture-content">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="culture-text"
            >
              <h2>Our Culture</h2>
              <p>At zustandtech, we believe in fostering a culture of innovation, collaboration, and continuous learning. We're not just building software; we're building the future.</p>
              <ul className="culture-list">
                <li>✨ Innovative mindset</li>
                <li>🤝 Collaborative environment</li>
                <li>📚 Continuous learning</li>
                <li>⚖️ Work-life balance</li>
                <li>🌍 Global impact</li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="culture-image"
            >
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
                alt="Team culture"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="career-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2>Don't see the right position?</h2>
            <p>Send us your resume and we'll keep you in mind for future opportunities</p>
            <Link to="/contact" className="btn btn-primary">
              Send Resume <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default CareerPage;